import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api";

const AddBranchItems = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [limit] = useState(10);
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const [filters, setFilters] = useState({
    branchid: branchId || "",
    storeid: "",
    itemcategoryid: "",
    itemname: "",
    itemcode: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    branchid: branchId || "",
    storeid: "",
    itemcategoryid: "",
    itemname: "",
    itemcode: "",
  });

  const initializedRef = useRef(false);
  const [isInitialReady, setIsInitialReady] = useState(false);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_BASE}/branchItems_branches`, {
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.branches)) {
        const rows = res.data.branches;
        setBranches(rows);

        const defaultBranch =
          branchId || rows?.[0]?.fld_Branchid?.toString() || "";

        setFilters((prev) => ({
          ...prev,
          branchid: prev.branchid || defaultBranch,
        }));

        return defaultBranch;
      }

      toast.error("❌ Unexpected branches response.");
      return "";
    } catch (err) {
      console.error("❌ Branch Fetch Error:", err);
      toast.error("❌ Failed to fetch branches.");
      return "";
    }
  };

  const fetchStores = async (selectedBranchId, preserveExisting = true) => {
    if (!selectedBranchId) {
      setStores([]);
      return "";
    }

    try {
      const res = await axios.get(`${API_BASE}/branchItems_stores`, {
        params: { branchid: selectedBranchId },
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.stores)) {
        const rows = res.data.stores;
        setStores(rows);

        const defaultStore = rows?.[0]?.fld_storeid?.toString() || "";

        setFilters((prev) => ({
          ...prev,
          storeid: preserveExisting ? prev.storeid || defaultStore : defaultStore,
        }));

        return preserveExisting ? filters.storeid || defaultStore : defaultStore;
      }

      toast.error("❌ Unexpected stores response.");
      return "";
    } catch (err) {
      console.error("❌ Store Fetch Error:", err);
      toast.error("❌ Failed to fetch stores.");
      return "";
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE}/branchItems_cat`, {
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.cat)) {
        setCategories(res.data.cat);
      } else {
        toast.error("❌ Unexpected categories response.");
      }
    } catch (err) {
      console.error("❌ Category Fetch Error:", err);
      toast.error("❌ Failed to fetch categories.");
    }
  };

  const fetchItems = async (searchFilters = appliedFilters, searchPage = page) => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/get_branch_items_list`, {
        params: {
          storeid: searchFilters.storeid || "",
          itemcategoryid: searchFilters.itemcategoryid || "",
          itemname: searchFilters.itemname || "",
          itemcode: searchFilters.itemcode || "",
          page: searchPage,
          pageSize: limit,
        },
        headers: commonHeaders,
      });

      const responseItems = Array.isArray(res.data?.items) ? res.data.items : [];
      const backendTotalCount =
        Number(res.data?.totalCount || res.data?.count || 0) || 0;

      setItems(responseItems);
      setTotalCount(backendTotalCount);

      const calculatedPages =
        backendTotalCount > 0 ? Math.ceil(backendTotalCount / limit) : 1;

      setTotalPages(calculatedPages);
    } catch (err) {
      console.error("❌ Error fetching items:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "❌ Failed to fetch items."
      );
      setItems([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const updateItemField = (itemid, field, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.itemid === itemid ? { ...item, [field]: value } : item
      )
    );
  };

  const handleBranchChange = async (value) => {
    setFilters((prev) => ({
      ...prev,
      branchid: value,
      storeid: "",
    }));

    const defaultStore = await fetchStores(value, false);

    setFilters((prev) => ({
      ...prev,
      branchid: value,
      storeid: defaultStore || "",
    }));
  };

  const handleSearch = () => {
    setPage(1);
    setSelectedItems([]);
    setAppliedFilters({ ...filters });
  };

  const handleAddSelected = async () => {
    if (selectedItems.length === 0) {
      toast.warning("⚠️ Please select items to add.");
      return;
    }

    const payload = selectedItems
      .map((id) => items.find((i) => i.itemid === id))
      .filter(Boolean)
      .map((item) => ({
        itemid: Number(item.itemid || 0),
        hsncode: item.hsncode || "",
        opstock: Number(item.fld_opening_stock || 0),
        minstock: Number(item.fld_minstock || 0),
        maxstock: Number(item.fld_maxstock || 0),
        price: Number(item.itemprice || 0),
        opdate: item.opdate || "",
        location: item.location || "",
        serialData: Array.isArray(item.serialData) ? item.serialData : [],
      }));

    const requestBody = {
      branchid: Number(filters.branchid || 0),
      storeid: Number(filters.storeid || 0),
      userid: Number(userId || 0),
      items: payload,
    };

    console.log("📤 Sending Add Items request:", requestBody);

    try {
      const response = await axios.post(
        `${API_BASE}/branchItems_add`,
        requestBody,
        {
          headers: commonHeaders,
        }
      );

      console.log("✅ Add Items Response:", response.data);

if (response.data?.success) {
        toast.success("✅ Items added successfully!");
        setSelectedItems([]);
        setTimeout(() => {
          navigate("/inventory/BranchItems");
        }, 1200);
      } else {
        toast.error(`❌ Add failed: ${response.data?.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("❌ Add Items API Error:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "❌ Submission failed."
      );
    }
  };

  useEffect(() => {
    const init = async () => {
      const defaultBranch = await fetchBranches();
      await fetchCategories();

      let defaultStore = "";
      if (defaultBranch) {
        defaultStore = await fetchStores(defaultBranch, false);
      }

      const initialFilters = {
        branchid: defaultBranch || branchId || "",
        storeid: defaultStore || "",
        itemcategoryid: "",
        itemname: "",
        itemcode: "",
      };

      setFilters(initialFilters);
      setAppliedFilters(initialFilters);
      setIsInitialReady(true);
    };

    if (!initializedRef.current) {
      initializedRef.current = true;
      init();
    }
  }, []);

  useEffect(() => {
    if (!isInitialReady) return;
    fetchItems(appliedFilters, page);
  }, [appliedFilters, page, isInitialReady]);

  return (
    <LayoutWrapper>
      <HeaderBar title="Branch Item Listing" setSkin={setSkin} currentSkin={skin} />

      <div
        className={`relative overflow-hidden rounded-3xl shadow-2xl border transition-all duration-300 ${
          isDark
            ? "bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-gray-900/95 backdrop-blur-xl border-gray-700/50 text-white"
            : "bg-gradient-to-br from-white via-gray-50/50 to-white backdrop-blur-sm border-gray-200/60 text-gray-900"
        }`}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className={`absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-10 ${
              isDark ? `bg-${baseColor}-400` : `bg-${baseColor}-200`
            }`}
          ></div>
          <div
            className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full opacity-10 ${
              isDark ? `bg-${baseColor}-500` : `bg-${baseColor}-300`
            }`}
          ></div>
        </div>

        <div className="relative z-10 p-8 border-b border-gray-200/20">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-200 dark:to-white bg-clip-text text-transparent">
                Branch Items Management
              </h1>
              <p className={`text-sm mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Manage and add items to branch inventory
              </p>
            </div>

            <button
              className={`group flex items-center gap-2 px-6 py-3 rounded-2xl text-white font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${buttonClass} ${hoverClass} focus:ring-4 focus:ring-${baseColor}-300/30`}
              onClick={() => navigate(-1)}
            >
              <span className="transition-transform group-hover:-translate-x-1">←</span>
              Back
            </button>
          </div>
        </div>

        <div className="relative z-10 p-8">
          <div
            className={`p-6 rounded-2xl border backdrop-blur-sm ${
              isDark
                ? "bg-gray-800/50 border-gray-700/50"
                : "bg-white/80 border-gray-200/60 shadow-lg"
            }`}
          >
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <span className="text-2xl">🔍</span>
              Search Filters
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium opacity-90">Branch</label>
                <select
                  className={`w-full border rounded-xl px-4 py-3 transition-all duration-200 focus:ring-4 focus:ring-${baseColor}-300/30 focus:border-${baseColor}-400 ${
                    isDark
                      ? "bg-gray-700/50 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                  value={filters.branchid}
                  onChange={(e) => handleBranchChange(e.target.value)}
                >
                  <option value="">Select Branch</option>
                  {branches.map((branchRow) => (
                    <option key={branchRow.fld_Branchid} value={branchRow.fld_Branchid}>
                      {branchRow.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium opacity-90">Store</label>
                <select
                  className={`w-full border rounded-xl px-4 py-3 transition-all duration-200 focus:ring-4 focus:ring-${baseColor}-300/30 focus:border-${baseColor}-400 ${
                    isDark
                      ? "bg-gray-700/50 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  value={filters.storeid}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, storeid: e.target.value }))
                  }
                >
                  <option value="">Select Store</option>
                  {stores.map((store) => (
                    <option key={store.fld_storeid} value={store.fld_storeid}>
                      {store.fld_storename}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium opacity-90">Category</label>
                <select
                  className={`w-full border rounded-xl px-4 py-3 transition-all duration-200 focus:ring-4 focus:ring-${baseColor}-300/30 focus:border-${baseColor}-400 ${
                    isDark
                      ? "bg-gray-700/50 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  value={filters.itemcategoryid}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      itemcategoryid: e.target.value,
                    }))
                  }
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.fld_itemcategoryid} value={cat.fld_itemcategoryid}>
                      {cat.fld_itemcategory_name || "(Unnamed Category)"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium opacity-90">Item Name</label>
                <input
                  className={`w-full border rounded-xl px-4 py-3 transition-all duration-200 focus:ring-4 focus:ring-${baseColor}-300/30 focus:border-${baseColor}-400 ${
                    isDark
                      ? "bg-gray-700/50 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                  placeholder="Search by name..."
                  value={filters.itemname}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, itemname: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium opacity-90">Item Code</label>
                <input
                  className={`w-full border rounded-xl px-4 py-3 transition-all duration-200 focus:ring-4 focus:ring-${baseColor}-300/30 focus:border-${baseColor}-400 ${
                    isDark
                      ? "bg-gray-700/50 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                  placeholder="Search by code..."
                  value={filters.itemcode}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, itemcode: e.target.value }))
                  }
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  className={`w-full px-6 py-3 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${buttonClass} ${hoverClass} focus:ring-4 focus:ring-${baseColor}-300/30`}
                >
                  🔍 Search
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 px-8 pb-8">
          <div
            className={`rounded-2xl border backdrop-blur-sm overflow-hidden ${
              isDark
                ? "bg-gray-800/50 border-gray-700/50"
                : "bg-white/90 border-gray-200/60 shadow-xl"
            }`}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-6 border-b border-gray-200/20">
              <div>
                <h3 className="text-lg font-semibold">Items List</h3>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {totalCount} items found
                </p>
              </div>

              <button
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                  selectedItems.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 hover:shadow-lg"
                } text-white`}
                onClick={handleAddSelected}
                disabled={selectedItems.length === 0}
              >
                <span className="text-lg">➕</span>
                Add Selected ({selectedItems.length})
              </button>
            </div>

<div className="overflow-x-auto min-w-0">
              <table className="w-full min-w-[1500px] text-sm">
                <thead
                  className={`${
                    isDark ? `bg-${baseColor}-900/50` : `bg-${baseColor}-${baseShade}`
                  } text-white`}
                >
                  <tr>
                    {[
                      "S.No",
                      "Code",
                      "Item Name",
                      "Part NO",
                      "UOM",
                      "OP Stock",
                      "Location",
                      "Price",
                      "Min.Stock",
                      "Max.Stock",
                      "OP Date",
                      "Serial No",
                      "HSN",
                      "Select",
                    ].map((header, index) => (
                      <th
                        key={index}
                        className="px-4 py-4 text-left font-semibold border-r border-white/10 last:border-r-0"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className={isDark ? "text-gray-200" : "text-gray-700"}>
                  {loading ? (
                    <tr>
                      <td colSpan="14" className="p-6">
                        <div className="space-y-3">
                          {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} height={40} className="rounded-lg" />
                          ))}
                        </div>
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="text-center py-12">
                        <div className="flex flex-col items-center gap-4">
                          <span className="text-6xl opacity-30">📦</span>
                          <p className="text-lg font-medium opacity-70">No items found</p>
                          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            Try adjusting your search filters
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.map((item, i) => (
                      <tr
                        key={item.itemid}
                        className={`border-b transition-colors duration-200 ${
                          isDark
                            ? "border-gray-700/50 hover:bg-gray-700/30"
                            : "border-gray-200/50 hover:bg-gray-50/80"
                        }`}
                      >
                        <td className="px-4 py-4 font-medium">
                          {(page - 1) * limit + i + 1}
                        </td>
                        <td className="px-4 py-4 font-mono text-xs">{item.itemcode}</td>
                        <td
                          className="px-4 py-4 font-medium max-w-[200px] truncate"
                          title={item.itemname}
                        >
                          {item.itemname}
                        </td>
                        <td className="px-4 py-4">{item.fld_itemPartNo || "-"}</td>
                        <td className="px-4 py-4">{item.itemuom}</td>

{[
                          {
                            field: "fld_opening_stock",
                            type: "number",
                            value: item.fld_opening_stock,
                            minWidth: "90px",
                          },
                          {
                            field: "location",
                            type: "text",
                            value: item.location,
                            minWidth: "130px",
                          },
                          {
                            field: "itemprice",
                            type: "number",
                            value: item.itemprice,
                            minWidth: "110px",
                          },
                          {
                            field: "fld_minstock",
                            type: "number",
                            value: item.fld_minstock,
                            minWidth: "100px",
                          },
                          {
                            field: "fld_maxstock",
                            type: "number",
                            value: item.fld_maxstock,
                            minWidth: "100px",
                          },
                          {
                            field: "opdate",
                            type: "date",
                            value: item.opdate,
                            minWidth: "150px",
                          },
                        ].map(({ field, type, value, minWidth }) => (
                          <td key={field} className="px-4 py-4">
                            <input
                              style={{ minWidth }}
                              className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-${baseColor}-300/50 ${
                                isDark
                                  ? "bg-gray-700/50 border-gray-600 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                              type={type}
                              value={value || ""}
                              onChange={(e) =>
                                updateItemField(item.itemid, field, e.target.value)
                              }
                            />
                          </td>
                        ))}

                        <td className="px-4 py-4">{item.serialno}</td>
<td className="px-4 py-4">
                          <input
                            style={{ minWidth: "100px" }}
                            className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-${baseColor}-300/50 ${
                              isDark
                                ? "bg-gray-700/50 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                            type="text"
                            value={item.hsncode || ""}
                            onChange={(e) =>
                              updateItemField(item.itemid, "hsncode", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            className={`w-5 h-5 rounded border-2 transition-all duration-200 focus:ring-2 focus:ring-${baseColor}-300/50 ${
                              selectedItems.includes(item.itemid)
                                ? `bg-${baseColor}-${baseShade} border-${baseColor}-${baseShade}`
                                : `border-gray-300 ${isDark ? "bg-gray-700" : "bg-white"}`
                            }`}
                            checked={selectedItems.includes(item.itemid)}
                            onChange={(e) =>
                              setSelectedItems((prev) =>
                                e.target.checked
                                  ? [...prev, item.itemid]
                                  : prev.filter((id) => id !== item.itemid)
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between p-6 border-t border-gray-200/20 gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Showing{" "}
                <span className={`font-semibold text-${baseColor}-600`}>
                  {items.length ? (page - 1) * limit + 1 : 0}
                </span>{" "}
                to{" "}
                <span className={`font-semibold text-${baseColor}-600`}>
                  {Math.min(page * limit, totalCount)}
                </span>{" "}
                of{" "}
                <span className={`font-semibold text-${baseColor}-600`}>
                  {totalCount}
                </span>{" "}
                results
              </div>

              {loading ? (
                <div className="flex space-x-2">
                  <Skeleton width={80} height={40} borderRadius={12} />
                  <Skeleton width={40} height={40} borderRadius={12} />
                  <Skeleton width={40} height={40} borderRadius={12} />
                  <Skeleton width={80} height={40} borderRadius={12} />
                </div>
              ) : (
                <div
                  className={`inline-flex rounded-2xl shadow-lg overflow-hidden border ${
                    isDark ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"
                  }`}
                >
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className={`px-6 py-3 text-sm font-medium transition-all duration-200 ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : `hover:bg-${baseColor}-50 text-gray-700 dark:hover:bg-${baseColor}-800/30 dark:text-gray-200`
                    }`}
                  >
                    ← Prev
                  </button>

                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (page <= 3) {
                      pageNum = idx + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = page - 2 + idx;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-4 py-3 text-sm font-medium transition-all duration-200 ${
                          page === pageNum
                            ? `bg-${baseColor}-${baseShade} text-white shadow-lg`
                            : `hover:bg-${baseColor}-50 text-gray-700 dark:hover:bg-${baseColor}-800/30 dark:text-gray-200`
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    className={`px-6 py-3 text-sm font-medium transition-all duration-200 ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : `hover:bg-${baseColor}-50 text-gray-700 dark:hover:bg-${baseColor}-800/30 dark:text-gray-200`
                    }`}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
        className="mt-16"
      />
    </LayoutWrapper>
  );
};

export default AddBranchItems;